"use client";

import React, { createContext, useState, ReactNode, useEffect, useCallback, Suspense } from "react";
import { usePathname, useRouter } from "next/navigation";
import { ThemeContextType } from "@/lib/Types";
// FCP FIX (task 4.2): useSearchParams() is no longer called here — it forced the
// whole page body to sit inside this provider's Suspense boundary. It now lives in
// the isolated leaf <SearchParamsHandler>, wrapped in its own <Suspense fallback={null}>
// below, so {children} stream immediately.
import { SearchParamsHandler } from "./SearchParamsHandler";

const defaultValue: ThemeContextType = {
  isQuoteOpen: false,
  handleOpenQuote: () => { },
  handleCloseQuote: () => { },
};

export const ThemeContext = createContext<ThemeContextType>(defaultValue);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({
  children,
}: ThemeProviderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);

  // Stable callback handed to the isolated SearchParamsHandler leaf.
  const handleQuoteParam = useCallback(() => setIsQuoteOpen(true), []);

  useEffect(() => {
    if (!isQuoteOpen) return;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isQuoteOpen]);

  const handleOpenQuote = () => {
    router.push(`${pathname}?request-a-quote`, { scroll: false });
    setIsQuoteOpen(true);
  };

  const handleCloseQuote = () => {
    router.replace(pathname, { scroll: false });
    setIsQuoteOpen(false);
  };

  return (
    <ThemeContext.Provider
      value={{
        isQuoteOpen,
        handleOpenQuote,
        handleCloseQuote,
      }}
    >
      <Suspense fallback={null}>
        <SearchParamsHandler onQuoteParam={handleQuoteParam} />
      </Suspense>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return React.useContext(ThemeContext);
}
